"use client";

import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { catalogApi, Category, ProductTemplate } from '@/lib/api/catalog';
import { salesApi, Cart } from '@/lib/api/sales';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  ShoppingCart,
  Star,
  Heart,
  Trash2,
  CheckCircle2,
  PackageCheck,
  Loader2
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export default function PartsStore() {
  const { toast } = useToast();

  // Data
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductTemplate[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);

  // State
  const [loading, setLoading] = useState(true);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [address, setAddress] = useState({
    name: '',
    phone: '',
    city: '',
    fullAddress: ''
  });

  useEffect(() => {
    fetchData();
    fetchCart();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    try {
      const cats = await catalogApi.getCategories();
      setCategories(cats);
    } catch (e) {
      console.error("Failed to load categories", e);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { store_type: 'parts_store,workshop' };
      if (selectedCategory) params.category = selectedCategory.toString();
      if (searchQuery) params.search = searchQuery;

      const res = await catalogApi.getProducts(params);
      setProducts(res.results);
    } catch (e) {
      console.error("Failed to load products", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    try {
      const c = await salesApi.getCart();
      setCart(c);
    } catch (e) {
      console.error("Failed to load cart", e);
    }
  };

  const addToCart = async (product: ProductTemplate) => {
    if (!product.variants || product.variants.length === 0) {
      toast({ variant: "destructive", title: "لا يمكن الإضافة", description: "هذا المنتج غير متوفر حالياً" });
      return;
    }

    // Add default variant
    const variantId = product.variants[0].id;
    try {
      const updatedCart = await salesApi.addToCart(variantId, 1);
      setCart(updatedCart);
      toast({ title: "تمت الإضافة", description: `تمت إضافة ${product.name_ar} إلى السلة` });
    } catch (e) {
      toast({ variant: "destructive", title: "خطأ", description: "حدث خطأ أثناء الإضافة للسلة" });
    }
  };

  const removeFromCart = async (lineId: number) => {
    try {
      const updatedCart = await salesApi.removeFromCart(lineId);
      setCart(updatedCart);
    } catch (e) {
      toast({ variant: "destructive", title: "خطأ", description: "تعذر إزالة المنتج" });
    }
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.name || !address.phone || !address.city) {
      toast({ variant: "destructive", title: "بيانات ناقصة", description: "يرجى إكمال بيانات العنوان والشحن" });
      return;
    }

    setSubmittingOrder(true);
    try {
      await salesApi.checkout({
        customer_name: address.name,
        customer_phone: address.phone,
        shipping_city: address.city,
        shipping_address: address.fullAddress,
        payment_method: 'cod'
      });

      setOrderCompleted(true);
      fetchCart(); // Clear cart in UI
      toast({ title: "تم تأكيد الطلب", description: "شكراً لثقتك! سيتم شحن طلبك قريباً." });
    } catch (e: any) {
      toast({ variant: "destructive", title: "فشل إتمام الطلب", description: e.response?.data?.error || "حدث خطأ غير متوقع" });
    } finally {
      setSubmittingOrder(false);
    }
  };

  if (orderCompleted) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation title="طلب مكتمل" />
        <div className="flex flex-col items-center justify-center p-8 mt-20 text-center space-y-6">
          <div className="bg-green-100 p-6 rounded-full text-green-600 animate-bounce">
            <CheckCircle2 className="h-16 w-16" />
          </div>
          <h2 className="text-3xl font-bold font-headline">تم استلام طلبك بنجاح!</h2>
          <p className="text-muted-foreground max-w-md">
            سيقوم فريقنا بتجهيز الطلب وشحنه إلى عنوانك في {address.city}. شكراً لتسوقك معنا!
          </p>
          <button onClick={() => setOrderCompleted(false)} className="bg-primary text-white rounded-2xl px-10 h-14 text-lg font-bold">
            العودة للمتجر
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation title="متجر قطع الغيار" />

      <div className="p-4 space-y-6 max-w-5xl mx-auto pb-24">
        <div className="flex flex-col gap-4">
          <div className="relative group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5 transition-colors group-focus-within:text-primary" />
            <Input
              className="pr-12 rounded-2xl bg-white border-none shadow-sm h-14 text-lg"
              placeholder="ابحث عن قطعة (بريمبو، دينسو، فحمات...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <Badge
              onClick={() => setSelectedCategory(null)}
              variant={selectedCategory === null ? "default" : "outline"}
              className={`px-6 py-2 rounded-full cursor-pointer whitespace-nowrap text-sm border-none shadow-sm transition-all ${selectedCategory === null ? 'bg-primary text-white' : 'bg-white hover:bg-slate-50 text-slate-500'
                }`}
            >
              الكل
            </Badge>
            {categories.map((cat) => (
              <Badge
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                className={`px-6 py-2 rounded-full cursor-pointer whitespace-nowrap text-sm border-none shadow-sm transition-all ${selectedCategory === cat.id ? 'bg-primary text-white' : 'bg-white hover:bg-slate-50 text-slate-500'
                  }`}
              >
                {cat.name_ar}
              </Badge>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : products.length === 0 ? (
          <div className="text-center p-12 text-slate-500">لا توجد منتجات تطابق بحثك.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card key={product.id} className="border-none shadow-sm hover:shadow-xl transition-all duration-300 rounded-3xl overflow-hidden group bg-white flex flex-col">
                <Link href={`/parts/${product.id}`} className="block relative aspect-square bg-slate-100 flex items-center justify-center p-4">
                  {product.images?.[0] ? (
                    <Image
                      src={product.images[0].image}
                      alt={product.name_ar}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="text-slate-300">لا توجد صورة</div>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute top-2 left-2 bg-white/50 backdrop-blur-md rounded-full text-foreground hover:bg-white hover:text-red-500 h-9 w-9"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Heart className="h-5 w-5" />
                  </Button>
                </Link>
                <CardContent className="p-4 flex-1">
                  <Link href={`/parts/${product.id}`}>
                    <h3 className="font-bold text-sm h-10 line-clamp-2 leading-snug mb-2 hover:text-primary transition-colors">{product.name_ar}</h3>
                  </Link>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-400 font-medium">{product.category_name}</div>
                    <div>
                      <span className="text-lg font-bold text-secondary">{product.base_price}</span>
                      <span className="text-[10px] text-muted-foreground mr-1">ر.س</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  <Button
                    onClick={() => addToCart(product)}
                    className="w-full rounded-2xl bg-slate-900 hover:bg-primary transition-colors flex gap-2 h-11 text-xs"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    أضف للسلة
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isCheckoutOpen} onOpenChange={setIsCheckoutOpen}>
        <DialogTrigger asChild>
          <div className="fixed bottom-6 left-6 z-50">
            <Button className="h-16 w-16 rounded-full shadow-2xl bg-secondary hover:bg-secondary/90 flex flex-col items-center justify-center p-0 scale-110 transition-transform active:scale-100">
              <div className="relative">
                <ShoppingCart className="h-7 w-7 text-white" />
                {cart && cart.item_count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-[10px] text-white font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-secondary">
                    {cart.item_count}
                  </span>
                )}
              </div>
            </Button>
          </div>
        </DialogTrigger>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-headline flex items-center gap-2">
              <PackageCheck className="h-6 w-6 text-primary" />
              سلة المشتريات وإتمام الطلب
            </DialogTitle>
          </DialogHeader>

          {!cart || cart.lines.length === 0 ? (
            <div className="py-12 text-center space-y-4">
              <p className="text-slate-500 font-medium">سلتك فارغة حالياً</p>
            </div>
          ) : (
            <form onSubmit={handlePlaceOrder} className="space-y-6">
              <div className="space-y-3">
                {cart.lines.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 bg-slate-50 p-3 rounded-2xl border">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-bold text-sm truncate">{item.variant_name}</h5>
                      <p className="text-xs text-muted-foreground">{item.unit_price} ر.س × {item.quantity}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => removeFromCart(item.id)}
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>الاسم الكامل</Label>
                    <Input
                      placeholder="أدخل اسمك"
                      required
                      value={address.name}
                      onChange={e => setAddress({ ...address, name: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>رقم الجوال</Label>
                    <Input
                      placeholder="05xxxxxxxx"
                      required
                      value={address.phone}
                      onChange={e => setAddress({ ...address, phone: e.target.value })}
                      className="rounded-xl text-left"
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>المدينة</Label>
                  <Input
                    placeholder="الرياض، جدة، الدمام..."
                    required
                    value={address.city}
                    onChange={e => setAddress({ ...address, city: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="bg-slate-900 text-white p-6 rounded-3xl">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xl font-bold">الإجمالي النهائي:</span>
                  <span className="text-3xl font-bold text-primary">{cart.total} ر.س</span>
                </div>
                <Button disabled={submittingOrder} type="submit" className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-lg font-bold">
                  {submittingOrder ? <Loader2 className="w-6 h-6 animate-spin" /> : 'تأكيد الطلب الآن'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
